import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../services/msalConfig";
import { CheckSSOUser } from "../services/apiService";
import toast from "react-hot-toast";
import { InteractionStatus } from "@azure/msal-browser";

const LoginPage = () => {
  const [error, setError] = useState("");
  const { instance, inProgress } = useMsal();
  const triggeredRef = useRef(false);

  const isSafari = () => {
    const ua = navigator.userAgent;
    return /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua) && !/Edg/.test(ua);
  };

  const denyAccess = (email) => {
    if (email) localStorage.setItem("ssoAccessDeniedEmail", email);
    window.location.href = "/unauthorized";
  };

  const finishLogin = async (account) => {
    if (!account?.username) {
      setError("No Microsoft account found.");
      toast.error("No Microsoft account found.");
      return;
    }
    try {
      const response = await CheckSSOUser(account.username);
      if (response && response.token) {
        localStorage.setItem("authToken", response.token);
        localStorage.setItem("isAuthenticated", "true");
        localStorage.removeItem("ssoAccessDeniedEmail");
        window.location.href = "/";
      } else {
        denyAccess(account.username);
      }
    } catch (err) {
      console.error("CheckSSOUser failed", err);
      denyAccess(account.username);
    }
  };

  const startLogin = async () => {
    try {
      if (isSafari()) {
        await instance.loginRedirect(loginRequest);
        return;
      }
      try {
        const loginResponse = await instance.loginPopup(loginRequest);
        instance.setActiveAccount(loginResponse.account);
        await finishLogin(loginResponse.account);
      } catch (popupErr) {
        if (popupErr.errorCode === "popup_window_error" || popupErr.errorCode === "empty_window_error") {
          await instance.loginRedirect(loginRequest);
          return;
        }
        throw popupErr;
      }
    } catch (err) {
      console.error("Login failed", err);
      triggeredRef.current = false;
      if (err.errorCode === "user_cancelled") {
        setError("Login cancelled.");
        toast.error("Login cancelled.");
      } else {
        setError("Login failed. Please try again.");
        toast.error("Login failed. Please try again.");
      }
    }
  };

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    if (triggeredRef.current) return;

    if (localStorage.getItem("ssoAccessDeniedEmail")) {
      window.location.href = "/unauthorized";
      return;
    }

    const accounts = instance.getAllAccounts();
    const hasToken = !!localStorage.getItem("authToken");

    if (accounts.length > 0 && hasToken) {
      window.location.href = "/";
      return;
    }

    if (accounts.length > 0 && !hasToken) {
      // Came back from redirect but backend rejected — treat as access denied
      triggeredRef.current = true;
      finishLogin(accounts[0]);
      return;
    }

    triggeredRef.current = true;
    startLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress]);

  return (
    <div className="flex flex-col min-h-screen w-full items-center justify-center bg-white">
      <img src="logo.svg" alt="Company Logo" className="h-12 mb-6" />
      {error ? (
        <>
          <p className="text-red-600 text-sm mb-4">{error}</p>
          <button
            onClick={() => {
              setError("");
              triggeredRef.current = true;
              startLogin();
            }}
            className="bg-primary-800 text-white px-4 py-2 rounded-md hover:bg-primary-800/70 text-sm"
          >
            Retry Microsoft Sign-in
          </button>
        </>
      ) : (
        <>
          <div className="w-10 h-10 border-4 border-primary-800 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-600 text-sm">Signing you in with Microsoft…</p>
        </>
      )}
    </div>
  );
};

export default LoginPage;
